// mergeMessages — backwards pagination merge for fn_conversation_read
// (p_before cursor). Shared by DmStream / ChannelStream / ThreadView.
//
// WHY THIS EXISTS: the live backend does NOT return `next_before` for a DM or
// channel read, so the cursor is the oldest loaded message's `at`. The naive
// merge `setMessages((prev) => [...older, ...prev])` assumes every older page
// is strictly older AND already ascending AND never overlaps the boundary. In
// production that assumption breaks three ways at once:
//   1. Duplicates — when p_before is inclusive (the boundary row is returned
//      again) or a page is refetched, the boundary message is appended twice.
//   2. Out of order — when a page comes back in a different order (or pages
//      overlap), the concatenated list is no longer monotonic by `at`.
//   3. Infinite same-cursor refetch — when a page is empty or its oldest `at`
//      equals the cursor, `messages[0].at` never advances, so loadMore reuses
//      the same p_before forever (seen in the network trace: two identical
//      p_before requests back to back).
// The merge below dedups by id, sorts ascending by `at`, and exposes a
// `pageExhausted` check so the caller can stop paging instead of looping.

// Stable ascending sort by `at`. Rows without a parseable `at` sink to the end
// in their original relative order (stable via the input order after dedupe).
function byAtAscending(a, b) {
  const ta = a?.at ? Date.parse(a.at) : NaN;
  const tb = b?.at ? Date.parse(b.at) : NaN;
  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;
  return ta - tb;
}

// Dedup by `id` when present (last occurrence wins so the most-recent page's
// copy is kept); fall back to a sender|at|body key for rows with no id so two
// legitimately-identical no-id rows still collapse.
export function dedupeById(rows) {
  const byId = new Map();
  const noId = new Map();
  for (const r of rows || []) {
    if (!r) continue;
    if (r.id != null && r.id !== "") {
      byId.set(r.id, r);
    } else {
      const key = `${r.from || r.writer_id || ""}|||${r.at || ""}|||${(r.body || "").slice(0, 80)}`;
      noId.set(key, r);
    }
  }
  return [...byId.values(), ...noId.values()];
}

// Prepend an older page and return a clean, ordered, de-duplicated list. Order
// is ALWAYS ascending by `at` regardless of the order the server returned, so
// the stream never renders out-of-chronological-order messages.
export function prependOlder(prev, older) {
  return dedupeById([...(older || []), ...(prev || [])]).sort(byAtAscending);
}

// The oldest `at` in a page — the next p_before cursor when the backend omits
// next_before (which it does for DM/channel reads).
export function oldestAt(rows) {
  let oldest = null;
  let oldestMs = null;
  for (const r of rows || []) {
    if (!r?.at) continue;
    const ms = Date.parse(r.at);
    if (Number.isNaN(ms)) continue;
    if (oldestMs === null || ms < oldestMs) {
      oldestMs = ms;
      oldest = r.at;
    }
  }
  return oldest;
}

// True when a page brought nothing strictly older than the cursor we just
// used — i.e. it is exhausted. Returning true lets the caller set hasMore=false
// and STOP instead of refetching the same p_before forever. A page counts as
// exhausted when:
//   - it is empty, OR
//   - every row is at-or-after the cursor (inclusive boundary returning rows we
//     already have, or the same page echoed back).
export function pageExhausted(older, cursor) {
  if (!older || older.length === 0) return true;
  if (!cursor) return false;
  const c = Date.parse(cursor);
  if (Number.isNaN(c)) return false;
  return older.every((r) => {
    if (!r?.at) return true; // untimestamped rows don't count as "older"
    const t = Date.parse(r.at);
    return Number.isNaN(t) || t >= c;
  });
}
